// Pantallas de acceso (UX §3): tarjeta centrada de 400 px (pantalla completa en teléfono) sobre el fondo,
// con el logo arriba y el nombre del negocio debajo. Sin riel.
import { Suspense, type ReactNode } from 'react';
import { Outlet } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { AuthConfigResponse } from '@atte/contracts';
import { Skeleton } from '@/components/ui/Skeleton';
import { AtteLogo } from '@/components/brand/AtteLogo';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';

/** Configuración pública del ingreso (`GET /auth/config`). */
export function useAuthConfig() {
  return useQuery({
    queryKey: qk.authConfig,
    queryFn: ({ signal }) => api.get<AuthConfigResponse>('/auth/config', { signal, skipGlobalHandlers: true }),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** `logo`: `ink` en el ingreso (DS §1.2); `brand` en "Sin acceso". */
export function AuthCard({ children, logo = 'ink' }: { children: ReactNode; logo?: 'ink' | 'brand' }) {
  const config = useAuthConfig();
  return (
    <div className="flex min-h-dvh flex-col items-stretch bg-bg sm:items-center sm:justify-center sm:px-4 sm:py-12">
      <div className="flex w-full flex-1 flex-col bg-surface px-5 pt-12 pb-8 sm:max-w-[400px] sm:flex-none sm:rounded-lg sm:border sm:border-border sm:px-8 sm:pt-10">
        <div className="mb-7 flex flex-col items-center text-center">
          <AtteLogo height={32} variant={logo} />
          <p className="m-0 mt-2 min-h-4 text-caption text-muted">{config.data?.businessName ?? ''}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Layout de las rutas de acceso (ingreso, recuperación, primer acceso, 2FA). */
export function AuthLayout() {
  return (
    <AuthCard>
      <Suspense fallback={<Skeleton variant="text" lines={5} />}>
        <Outlet />
      </Suspense>
    </AuthCard>
  );
}
